import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLang } from "@/lib/i18n";
import { useSiteConfig } from "@/context/SiteConfigContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let id = sessionStorage.getItem("eleven_chat_session");
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    sessionStorage.setItem("eleven_chat_session", id);
  }
  return id;
}

/* --- Assistant message rendering: bold, labelled internal links, lists --- */

const PATH_RE_SRC = "\\/(?:anfrage\\/[a-z-]+|berater-finden|termin|karriere|privatkunden|leistungen(?:\\/[a-z-]+)?|ueber-uns)";

const PATH_LABELS = {
  "/anfrage/rueckruf": { de: "R\u00fcckruf anfragen", en: "Request a callback" },
  "/anfrage/nettopolice": { de: "Nettopolice anfragen", en: "Net policy inquiry" },
  "/anfrage/vertragspruefung": { de: "Vertrag pr\u00fcfen lassen", en: "Contract review" },
  "/anfrage/immobilien": { de: "Immobilien-Anfrage", en: "Real estate inquiry" },
  "/anfrage/festgeld": { de: "Festgeld-Anfrage", en: "Fixed-term inquiry" },
  "/anfrage/beraterbewerbung": { de: "Als Berater bewerben", en: "Apply as advisor" },
  "/anfrage/partner": { de: "Partner werden", en: "Become a partner" },
  "/berater-finden": { de: "Berater finden", en: "Find an advisor" },
  "/termin": { de: "Termin buchen", en: "Book an appointment" },
  "/karriere": { de: "Karriere", en: "Careers" },
  "/privatkunden": { de: "Privatkunden", en: "Private clients" },
  "/leistungen": { de: "Leistungen", en: "Services" },
  "/leistungen/nettopolicen": { de: "Nettopolicen", en: "Net policies" },
  "/leistungen/immobilien": { de: "Immobilien", en: "Real estate" },
  "/leistungen/festgeld": { de: "Festgeld", en: "Fixed-term deposits" },
  "/ueber-uns": { de: "\u00dcber uns", en: "About us" },
};

function pathLabel(path, lang) {
  const entry = PATH_LABELS[path];
  if (!entry) return path;
  return lang === "en" ? entry.en : entry.de;
}

function ChatLink({ to, label }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-0.5 font-medium text-[#111318] underline decoration-[#FF3C20] decoration-2 underline-offset-2 hover:text-[#E8241B] transition-colors"
      data-testid="chat-internal-link"
    >
      {label}
      <ArrowUpRight className="h-3 w-3 shrink-0" />
    </Link>
  );
}

/* Inline renderer: markdown links [label](/path), **bold**, raw internal paths. */
function renderInline(text, lang, keyPrefix) {
  const re = new RegExp(
    `\\[([^\\]]+)\\]\\((${PATH_RE_SRC})\\)|\\*\\*([^*]+)\\*\\*|(${PATH_RE_SRC})`,
    "g"
  );
  const nodes = [];
  let last = 0;
  let m;
  let k = 0;
  const pushPlain = (s) => {
    // strip stray markdown asterisks that are not part of a complete token
    const cleaned = s.replace(/\*\*/g, "").replace(/^#+\s*/gm, "");
    if (cleaned) nodes.push(<span key={`${keyPrefix}-t${k++}`}>{cleaned}</span>);
  };
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) pushPlain(text.slice(last, m.index));
    if (m[1] && m[2]) {
      nodes.push(<ChatLink key={`${keyPrefix}-l${k++}`} to={m[2]} label={m[1]} />);
    } else if (m[3] !== undefined) {
      const inner = m[3].trim();
      const onlyPath = inner.match(new RegExp(`^(${PATH_RE_SRC})$`));
      if (onlyPath) {
        nodes.push(<ChatLink key={`${keyPrefix}-l${k++}`} to={onlyPath[1]} label={pathLabel(onlyPath[1], lang)} />);
      } else {
        nodes.push(
          <strong key={`${keyPrefix}-b${k++}`} className="font-semibold text-[#111318]">
            {inner}
          </strong>
        );
      }
    } else if (m[4]) {
      nodes.push(<ChatLink key={`${keyPrefix}-l${k++}`} to={m[4]} label={pathLabel(m[4], lang)} />);
    }
    last = re.lastIndex;
  }
  if (last < text.length) pushPlain(text.slice(last));
  return nodes;
}

/* Renders assistant text: paragraphs, bullet lists, bold and labelled links. */
function AssistantText({ text, lang }) {
  const lines = text.replace(/\r/g, "").split("\n");
  const blocks = [];
  let list = null;
  lines.forEach((line) => {
    const lm = line.match(/^\s*[-\u2022*]\s+(.*)/);
    if (lm && !line.trim().startsWith("**")) {
      if (!list) {
        list = [];
        blocks.push({ type: "ul", items: list });
      }
      list.push(lm[1]);
    } else {
      list = null;
      blocks.push({ type: "p", text: line });
    }
  });
  return (
    <div className="space-y-1">
      {blocks.map((b, i) =>
        b.type === "ul" ? (
          <ul key={i} className="my-1 space-y-1">
            {b.items.map((item, j) => (
              <li key={j} className="flex gap-2">
                <span className="mt-[7px] h-1 w-1 rounded-full bg-[#FF3C20] shrink-0" />
                <span>{renderInline(item, lang, `${i}-${j}`)}</span>
              </li>
            ))}
          </ul>
        ) : b.text.trim() === "" ? (
          <div key={i} className="h-1" />
        ) : (
          <p key={i}>{renderInline(b.text, lang, `${i}`)}</p>
        )
      )}
    </div>
  );
}

export default function ChatWidget() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const site = useSiteConfig();
  const brand = site.brand_name || "Eleven Family Office";
  const scrollRef = useRef(null);
  const openedOnce = useRef(false);

  const suggestions = [
    t(`Was macht ${brand}?`, `What does ${brand} do?`),
    t("Was ist eine Nettopolice?", "What is a net policy?"),
    t("Wie l\u00e4uft die Beratung ab?", "How does the advice process work?"),
    t("Wie kann ich einen Termin buchen?", "How can I book an appointment?"),
  ];

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  useEffect(() => {
    if (open && !openedOnce.current) {
      openedOnce.current = true;
      setMessages([
        {
          role: "assistant",
          content: t(
            `Guten Tag! Ich bin der ${brand} Assistent. Ich beantworte Ihre Fragen zu unseren Leistungen, zum Beratungsablauf und zu Ihren Anfragewegen. Wie kann ich helfen?`,
            `Hello! I'm the ${brand} Assistant. I answer your questions about our services, the advice process and how to get in touch. How can I help?`
          ),
        },
      ]);
    }
  }, [open, t]);

  const send = useCallback(
    async (text) => {
      const trimmed = (text || "").trim();
      if (!trimmed || streaming) return;
      setInput("");
      setMessages((m) => [...m, { role: "user", content: trimmed }, { role: "assistant", content: "" }]);
      setStreaming(true);
      try {
        const res = await fetch(`${API}/public/chat/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: getSessionId(), message: trimmed, locale: lang }),
        });
        if (!res.ok || !res.body) throw new Error("stream failed");
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n\n");
          buffer = lines.pop() || "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const data = line.slice(6);
            if (data === "[DONE]") continue;
            try {
              const parsed = JSON.parse(data);
              if (parsed.delta) {
                setMessages((m) => {
                  const copy = [...m];
                  copy[copy.length - 1] = {
                    role: "assistant",
                    content: copy[copy.length - 1].content + parsed.delta,
                  };
                  return copy;
                });
              }
            } catch (_) {
              /* ignore malformed chunk */
            }
          }
        }
      } catch (e) {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = {
            role: "assistant",
            content: t(
              "Entschuldigung, es gab ein technisches Problem. Bitte versuchen Sie es sp\u00e4ter erneut.",
              "Sorry, there was a technical problem. Please try again later."
            ),
          };
          return copy;
        });
      } finally {
        setStreaming(false);
      }
    },
    [lang, streaming, t]
  );

  return (
    <>
      {/* Floating button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-5 right-5 z-[70] h-14 w-14 rounded-full bg-[#111318] text-white flex items-center justify-center hover:bg-[#07090D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF3C20] active:scale-95 transition-colors"
            aria-label={t("Chat \u00f6ffnen", "Open chat")}
            data-testid="chat-widget-button"
          >
            <MessageCircle className="h-6 w-6" />
            <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-[#FF3C20] border-2 border-white" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
            className="fixed bottom-4 right-4 left-4 sm:left-auto sm:right-5 z-[70] sm:w-[390px] rounded-[8px] border border-border bg-white overflow-hidden flex flex-col"
            style={{ height: "min(600px, calc(100vh - 40px))" }}
            data-testid="chat-widget-panel"
          >
            {/* Header */}
            <div className="bg-[#111318] px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div>
                  <img src="/brand/eleven-logo-light-sm.png" alt="Eleven Family Office" className="h-[14px] w-auto" />
                  <p className="mt-1.5 text-[11px] text-white/50">{t("Assistent \u2013 beantwortet Fragen zur Website", "Assistant \u2013 answers questions about the site")}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full text-white/60 hover:text-white hover:bg-white/10"
                onClick={() => setOpen(false)}
                aria-label={t("Chat schlie\u00dfen", "Close chat")}
                data-testid="chat-close-button"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#F6F7F8]">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-[8px] px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#111318] text-white rounded-br-md"
                        : "bg-white border border-border text-[#2A303A] rounded-bl-md"
                    }`}
                    data-testid={m.role === "user" ? "chat-message-user" : "chat-message-assistant"}
                  >
                    {m.role === "assistant" ? <AssistantText text={m.content} lang={lang} /> : m.content}
                    {m.role === "assistant" && m.content === "" && streaming && i === messages.length - 1 && (
                      <span className="inline-flex gap-1 items-center py-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:0ms]" />
                        <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:120ms]" />
                        <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:240ms]" />
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {messages.length === 1 && !streaming && (
                <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-suggestions">
                  {suggestions.map((s, i) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="rounded-full border border-border bg-white px-3.5 py-2 text-xs font-medium text-[#3B4452] hover:border-[#FF3C20]/60 hover:text-[#111318] transition-colors"
                      data-testid={`chat-suggestion-${i + 1}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-border bg-white p-3 shrink-0">
              <form
                className="flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  send(input);
                }}
              >
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={t("Ihre Frage\u2026", "Your question\u2026")}
                  maxLength={1000}
                  className="h-11 rounded-[6px] flex-1"
                  data-testid="chat-input"
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={streaming || !input.trim()}
                  className="h-11 w-11 rounded-[6px] bg-[#FF3C20] text-white hover:bg-[#E8241B] shrink-0"
                  aria-label={t("Senden", "Send")}
                  data-testid="chat-send-button"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </form>
              <p className="mt-2 text-[10px] text-[#9AA1AB] leading-relaxed text-center">
                {t(
                  "KI-Assistent \u2013 keine Anlage-, Steuer- oder Rechtsberatung. Konditionen nur nach individueller Pr\u00fcfung.",
                  "AI assistant \u2013 no investment, tax or legal advice. Conditions only after individual review."
                )}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
